import { createContext, useContext, type ReactNode } from 'react';
import { DEFAULT_DATE_FORMAT, DEFAULT_TILE_LIST_ROWS, documentDefaultDay, type DateFormat } from '@bms2/shared';

/**
 * The zone every screen inside the frame renders its times in (piece 12;
 * decision 0261), and the Organization's date format beside it (piece 15).
 * 🔨 A control reads them from here and never from
 * `Intl.DateTimeFormat().resolvedOptions()`: the browser's guess changes when
 * the person travels, and the setting is a choice they made once.
 *
 * 💰 The default is UTC rather than the browser's zone, so a control rendered
 * outside the frame is obviously unconfigured instead of quietly plausible.
 */
const ViewerZoneContext = createContext<string>('UTC');
const OrganizationZoneContext = createContext<string | null>(null);
const DateFormatContext = createContext<DateFormat>(DEFAULT_DATE_FORMAT);
/**
 * 🔒 How many rows a tile list draws before Show more (decision 5960): the
 * Organization's Tile list rows, read once with the zone and the date format,
 * and four outside the frame.
 */
const TileListRowsContext = createContext<number>(DEFAULT_TILE_LIST_ROWS);

export function ViewerZoneProvider({ timeZone, organizationTimeZone = timeZone, dateFormat = DEFAULT_DATE_FORMAT, tileListRows = DEFAULT_TILE_LIST_ROWS, children }: { timeZone: string; organizationTimeZone?: string; dateFormat?: DateFormat; tileListRows?: number; children: ReactNode }) {
  return (
    <ViewerZoneContext.Provider value={timeZone}>
      <OrganizationZoneContext.Provider value={organizationTimeZone}>
        <DateFormatContext.Provider value={dateFormat}>
          <TileListRowsContext.Provider value={tileListRows}>{children}</TileListRowsContext.Provider>
        </DateFormatContext.Provider>
      </OrganizationZoneContext.Provider>
    </ViewerZoneContext.Provider>
  );
}

export function useViewerZone(): string {
  return useContext(ViewerZoneContext);
}

/**
 * 🔒 The day every document form opens its date on: today in the viewer's own
 * zone (decision 1030). The one place a form reads it, so no form is left on
 * the server's day in UTC or the machine's clock.
 */
export function useDocumentToday(): string {
  return documentDefaultDay(new Date(), useViewerZone());
}

/**
 * 🔒 Today in the Organization's zone (T3448): the day a standard form's date is held to, because the server refuses a form
 * dated after today in that zone. Outside the frame it reads the viewer's zone, as `useDocumentToday` does.
 */
export function useOrganizationToday(): string {
  const organizationZone = useContext(OrganizationZoneContext);
  const viewerZone = useViewerZone();
  return documentDefaultDay(new Date(), organizationZone ?? viewerZone);
}

export function useDateFormat(): DateFormat {
  return useContext(DateFormatContext);
}

/** The rows a tile whose items are a list draws before it offers the rest (decision 5960). */
export function useTileListRows(): number {
  return useContext(TileListRowsContext);
}
